import React, { useState, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Save, Mail } from 'lucide-react';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { toast } from 'sonner';
import { fetchObjetivos, upsertObjetivo } from '@/services/api';

/**
 * Admin tab — edit OBJETIVOS_RESPONSABLES_2026 overrides without touching code.
 * The static seed defined in `backend/config.py` is the floor; this UI lets
 * the admin add an email + tweak monthly targets per (zona, responsable) row.
 * Overrides are stored in MongoDB collection `objetivos_overrides`.
 */
export function ObjetivosTab() {
  const qc = useQueryClient();
  const { data, isLoading } = useQuery({ queryKey: ['objetivos'], queryFn: fetchObjetivos });
  const [drafts, setDrafts] = useState({});  // key = `${zona}|${responsable}` → partial overrides

  useEffect(() => {
    // Seed drafts state when data first loads (so edits feel responsive)
    if (data?.objetivos && Object.keys(drafts).length === 0) {
      const seeded = {};
      data.objetivos.forEach((o) => {
        seeded[`${o.zona}|${o.responsable}`] = {
          email: o.email || '',
          objetivo_ops_mes: o.objetivo_ops_mes,
          objetivo_liderazgo_mes: o.objetivo_liderazgo_mes,
          encargados: o.encargados,
        };
      });
      setDrafts(seeded);
    }
  }, [data]); // eslint-disable-line react-hooks/exhaustive-deps

  const saveMut = useMutation({
    mutationFn: upsertObjetivo,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['objetivos'] });
      qc.invalidateQueries({ queryKey: ['cumplimiento-responsables'] });
      toast.success('Objetivos guardados');
    },
    onError: (e) => toast.error(e?.response?.data?.detail || 'Error guardando objetivos'),
  });

  if (isLoading) return <Skeleton className="h-72 w-full" />;
  const rows = data?.objetivos || [];

  const setDraft = (key, field, value) => {
    setDrafts((d) => ({ ...d, [key]: { ...d[key], [field]: value } }));
  };

  const save = (zona, responsable) => {
    const key = `${zona}|${responsable}`;
    const dr = drafts[key] || {};
    saveMut.mutate({
      zona, responsable,
      email: dr.email || null,
      objetivo_ops_mes: dr.objetivo_ops_mes != null ? Number(dr.objetivo_ops_mes) : null,
      objetivo_liderazgo_mes: dr.objetivo_liderazgo_mes != null ? Number(dr.objetivo_liderazgo_mes) : null,
      encargados: dr.encargados != null ? Number(dr.encargados) : null,
    });
  };

  return (
    <div className="space-y-4" data-testid="admin-objetivos-tab">
      <div>
        <h3 className="font-montserrat font-bold text-sm" style={{ color: 'var(--vz-text-primary)' }}>
          Objetivos mensuales por responsable (Luis 2026)
        </h3>
        <p className="text-[11px] mt-1" style={{ color: 'var(--vz-text-muted)' }}>
          Edita el email del gerente (para recordatorios automáticos) y, si necesitas ajustar, los
          objetivos mensuales de OPS / Liderazgo. Los valores en blanco usan el seed por defecto.
        </p>
      </div>

      <div className="rounded-xl overflow-hidden" style={{ border: '1px solid var(--vz-border-subtle)' }}>
        <table className="w-full text-xs">
          <thead>
            <tr style={{ background: 'var(--vz-bg-elevated)', borderBottom: '1px solid var(--vz-border-subtle)' }}>
              <th className="text-left py-2 px-3 font-semibold" style={{ color: 'var(--vz-text-muted)' }}>Zona</th>
              <th className="text-left py-2 px-3 font-semibold" style={{ color: 'var(--vz-text-muted)' }}>Responsable</th>
              <th className="text-left py-2 px-3 font-semibold flex items-center gap-1" style={{ color: 'var(--vz-text-muted)' }}>
                <Mail size={11} /> Email
              </th>
              <th className="text-right py-2 px-3 font-semibold" style={{ color: 'var(--vz-text-muted)' }}>OPS/mes</th>
              <th className="text-right py-2 px-3 font-semibold" style={{ color: 'var(--vz-text-muted)' }}>Lid/mes</th>
              <th className="text-right py-2 px-3 font-semibold" style={{ color: 'var(--vz-text-muted)' }}>Encargados</th>
              <th className="text-center py-2 px-3 font-semibold" style={{ color: 'var(--vz-text-muted)' }}>Acción</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((r) => {
              const key = `${r.zona}|${r.responsable}`;
              const dr = drafts[key] || {};
              return (
                <tr key={key} style={{ borderBottom: '1px solid var(--vz-border-subtle)' }} data-testid={`obj-row-${r.responsable}-${r.zona}`}>
                  <td className="py-1.5 px-3" style={{ color: 'var(--vz-text-muted)' }}>{r.zona}</td>
                  <td className="py-1.5 px-3" style={{ color: 'var(--vz-text-primary)' }}>{r.responsable}</td>
                  <td className="py-1.5 px-3">
                    <Input
                      type="email"
                      value={dr.email ?? ''}
                      onChange={(e) => setDraft(key, 'email', e.target.value)}
                      placeholder="nombre@valorizasm.com"
                      className="h-7 text-xs w-64"
                      data-testid={`obj-email-${r.responsable}`}
                    />
                  </td>
                  <td className="py-1.5 px-3">
                    <Input
                      type="number" min="0"
                      value={dr.objetivo_ops_mes ?? ''}
                      onChange={(e) => setDraft(key, 'objetivo_ops_mes', e.target.value)}
                      className="h-7 text-xs w-16 text-right tabular-nums"
                    />
                  </td>
                  <td className="py-1.5 px-3">
                    <Input
                      type="number" min="0"
                      value={dr.objetivo_liderazgo_mes ?? ''}
                      onChange={(e) => setDraft(key, 'objetivo_liderazgo_mes', e.target.value)}
                      className="h-7 text-xs w-16 text-right tabular-nums"
                    />
                  </td>
                  <td className="py-1.5 px-3">
                    <Input
                      type="number" min="0"
                      value={dr.encargados ?? ''}
                      onChange={(e) => setDraft(key, 'encargados', e.target.value)}
                      className="h-7 text-xs w-16 text-right tabular-nums"
                    />
                  </td>
                  <td className="py-1.5 px-3 text-center">
                    <Button
                      size="sm"
                      variant="outline"
                      className="h-7 text-[10px] gap-1"
                      onClick={() => save(r.zona, r.responsable)}
                      disabled={saveMut.isPending}
                      data-testid={`obj-save-${r.responsable}`}
                    >
                      <Save size={10} /> Guardar
                    </Button>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}
